// 3rd party
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { flowResult } from 'mobx';
import { CancellablePromise } from 'mobx/dist/api/flow';
import { observer } from 'mobx-react';

// Shared UI
import { LocalizedText, useLocalization } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { UitkCard } from 'uitk-react-cards';
import { UitkFakeInput, UitkTextInput } from 'uitk-react-input';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLoader } from 'uitk-react-loader';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';

// Components
import { CopyButton } from 'components/quicklinks';

// Hooks
import { useStores } from 'hooks';

// Models
import { LinkAnalyticsMicroMessageContent } from 'models/analytics';
import { LinkType } from 'models/analytics/enums';

// Utils
import {
  analyticsEvent,
  getLinkAnalyticsMicroMessage,
  getProductAnalyticsMicroMessage,
  getProgramAnalyticsMicroMessage,
  getUserAnalyticsMicroMessage,
} from '../../util/analytics';

const CustomQuicklinkV2 = observer(
  // tslint:disable-next-line:no-shadowed-variable
  function CustomQuicklinkV2(): JSX.Element {
    const l10n = useLocalization();
    const { analytics, productsV2, pzStore, quicklinks, user, userAuth } = useStores();

    const { activeProduct } = productsV2;
    const { pzPublisher } = pzStore;
    const { customQuicklink } = quicklinks;
    const { activeProgram } = user;
    const { currentUserDetails } = userAuth;

    const {
      control,
      formState: { errors },
      handleSubmit,
    } = useForm({ mode: 'onBlur' });

    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const result = useRef<CancellablePromise<unknown>>();

    const analyticsTrackEvents = useCallback(
      // tslint:disable-next-line:no-shadowed-variable
      function analyticsTrackEvents(rfrr: string, link?: LinkAnalyticsMicroMessageContent) {
        const microMessages = [
          getProgramAnalyticsMicroMessage(activeProgram, activeProgram?.programStatus),
          getProductAnalyticsMicroMessage(activeProduct),
        ];
        const microMessagesWithLink = [...microMessages, getLinkAnalyticsMicroMessage(link)];

        const filterAnalyticsTrackEvent = {
          'affhub_copy_code.selected': () => {
            analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Copy Code Selected', microMessagesWithLink);
          },
          'affhub_copy_link.selected': () => {
            analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Copy Link Selected', microMessagesWithLink);
          },
          'affhub_link_create.selected': () => {
            analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Link Create Selected', microMessages);
          },
          'affhub_link_create.succeeded': () => {
            analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Link Create Succeeded', microMessagesWithLink);
          },
          'affhub_link_create.failed': () => {
            analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Link Create Failed', microMessagesWithLink);
          },
        };

        filterAnalyticsTrackEvent[rfrr]();
      },
      [analytics, activeProgram, activeProduct, currentUserDetails, pzPublisher],
    );

    function buildLinkAnalyticsMicroMessageContent(linkTargetUrl: string): LinkAnalyticsMicroMessageContent {
      return {
        linkType: LinkType.CUSTOM,
        linkIndex: 0,
        linkTargetUrl,
      };
    }

    function trackSubmitClick() {
      analyticsTrackEvents('affhub_link_create.selected');
    }

    /* istanbul ignore next */
    function handleClear() {
      setUrl('');
    }

    async function onFormSubmitValid({ url: submittedUrl }) {
      setIsLoading(true);

      submittedUrl = submittedUrl.trim();
      submittedUrl = submittedUrl.replace('https://', '').replace('http://', '');
      submittedUrl = `https://${submittedUrl}`;

      // await flowResult(quicklinks.fetchCustomQuicklink(submittedUrl, activeProduct, activeProgram));
      result.current = flowResult(quicklinks.fetchCustomQuicklink(submittedUrl, activeProduct, activeProgram));
      await result.current;

      setIsLoading(false);

      analyticsTrackEvents('affhub_link_create.succeeded', buildLinkAnalyticsMicroMessageContent(submittedUrl));
    }

    function onFormSubmitInvalid() {
      analyticsTrackEvents('affhub_link_create.failed', buildLinkAnalyticsMicroMessageContent(url));
    }

    const onFormSubmit = handleSubmit(onFormSubmitValid, onFormSubmitInvalid);

    useEffect(
      function onMount() {
        // result.current = flowResult(quicklinks.fetchCustomQuicklink(url, activeProduct, activeProgram));

        return function onUnmount() {
          result.current?.cancel();
          quicklinks.resetCustomQuicklink();
        };
      },
      // [quicklinks, url, activeProduct, activeProgram],
      [quicklinks],
    );

    return (
      <>
        <UitkSpacing margin={{ blockend: 'one' }}>
          <UitkHeading tag="h2" size={4}>
            <LocalizedText message="product.quicklink.custom.title.custom_tracking_link" />
          </UitkHeading>
        </UitkSpacing>

        <UitkSpacing margin={{ blockend: 'six' }}>
          <UitkText>
            <LocalizedText message="product.quicklink.custom.content.paste_url" data={[activeProgram.title]} />
          </UitkText>
        </UitkSpacing>

        <UitkSpacing margin={{ blockend: 'six' }}>
          <UitkCard padded={true}>
            <form name="CustomQuicklink" onSubmit={onFormSubmit}>
              <UitkLayoutGrid columns={{ small: 1, large: 4 }} space="four">
                <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
                  <UitkSpacing padding={{ blockstart: 'three' }}>
                    <UitkText>
                      <LocalizedText
                        message="product.quicklink.custom.url.label.enter_url"
                        data={[activeProgram.title]}
                      />
                    </UitkText>
                  </UitkSpacing>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem colSpan={{ small: 1, large: 3 }}>
                  <UitkLayoutFlex space="two" className="QuicklinkInputs">
                    <UitkLayoutFlexItem grow={1}>
                      <UitkLayoutFlex direction="column">
                        <Controller
                          control={control}
                          name="url"
                          defaultValue=""
                          rules={{
                            required: l10n.formatText('product.quicklink.custom.url.error.missing_url'),
                            validate: (inputUrl) => {
                              if (!new RegExp(activeProgram.validUrlPattern).test(inputUrl.trim())) {
                                return l10n.formatText('product.quicklink.custom.url.error.invalid_url', [
                                  activeProduct.data.validUrl,
                                ]);
                              }
                            },
                          }}
                          render={({ field: { onChange, onBlur, value, name } }) => (
                            <UitkTextInput
                              id="CustomQuicklinkUrlInput"
                              placeholder={l10n.formatText('product.quicklink.custom.url.placeholder')}
                              name={name}
                              label="url"
                              value={url}
                              onChange={({ target: { value: newValue } }) => {
                                setUrl(newValue);
                                onChange(newValue);
                              }}
                              onBlur={onBlur}
                              hasVisibleLabel={false}
                              handleClear={handleClear}
                              hasClearButton={true}
                              data-cy="quicklink-url-field"
                              invalid={errors.url?.message}
                            />
                          )}
                        />
                      </UitkLayoutFlex>
                    </UitkLayoutFlexItem>

                    <UitkLayoutFlexItem alignSelf="start">
                      <UitkPrimaryButton
                        id="CustomQuicklinkSubmitButton"
                        type="submit"
                        className="QuicklinkButton"
                        disabled={errors.length > 0 || isLoading}
                        size={UitkButtonSize.LARGE}
                        data-cy="quicklink-submit"
                        onClick={trackSubmitClick}
                      >
                        {(isLoading && <UitkLoader isSmall={true} isDarkBackground={true} />) ||
                          (customQuicklink?.link && l10n.formatText('product.quicklink.custom.form.actions.update')) ||
                          l10n.formatText('product.quicklink.custom.form.actions.submit')}
                      </UitkPrimaryButton>
                    </UitkLayoutFlexItem>
                  </UitkLayoutFlex>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>

              {customQuicklink?.link && (
                <UitkLayoutGrid columns={{ small: 1, large: 4 }} space="four">
                  <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
                    <div />
                  </UitkLayoutGridItem>

                  <UitkLayoutGridItem colSpan={{ small: 1, large: 3 }}>
                    <div>
                      <UitkSpacing margin={{ block: 'four' }}>
                        <UitkHeading tag="h4" size={6}>
                          <LocalizedText message="product.quicklink.custom.content.here_is_your_link" />
                        </UitkHeading>
                      </UitkSpacing>

                      <UitkLayoutGrid columns={{ small: 1, large: 2 }} space="four">
                        <UitkLayoutGridItem>
                          <div>
                            <UitkLayoutFlex space="two" className="QuicklinkInputs" data-stid="copy-custom-link">
                              <UitkFakeInput
                                id="CustomQuicklinkLink"
                                label={l10n.formatText('product.quicklink.form.link')}
                                value={customQuicklink.link}
                                data-cy="quicklink-url-field"
                                data-stid="copy-custom-link-input"
                              />

                              <CopyButton
                                value={customQuicklink.link}
                                onCopyClick={() =>
                                  analyticsTrackEvents(
                                    'affhub_copy_link.selected',
                                    buildLinkAnalyticsMicroMessageContent(customQuicklink.link),
                                  )
                                }
                              />
                            </UitkLayoutFlex>
                          </div>
                        </UitkLayoutGridItem>

                        <UitkLayoutGridItem>
                          <div>
                            <UitkLayoutFlex space="two" className="QuicklinkInputs" data-stid="copy-custom-code">
                              <UitkFakeInput
                                id="CustomQuicklinkCode"
                                label={l10n.formatText('product.quicklink.form.code')}
                                value={customQuicklink.code}
                                data-cy="quicklink-url-field"
                                data-stid="copy-custom-code-input"
                              />

                              <CopyButton
                                value={customQuicklink.code}
                                onCopyClick={() =>
                                  analyticsTrackEvents(
                                    'affhub_copy_code.selected',
                                    buildLinkAnalyticsMicroMessageContent(customQuicklink.code),
                                  )
                                }
                              />
                            </UitkLayoutFlex>
                          </div>
                        </UitkLayoutGridItem>
                      </UitkLayoutGrid>
                    </div>
                  </UitkLayoutGridItem>
                </UitkLayoutGrid>
              )}
            </form>
          </UitkCard>
        </UitkSpacing>
      </>
    );
  },
);

export { CustomQuicklinkV2 };
